import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { prop, ifProp } from 'styled-tools';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import NewsBlogFeed from 'components/NewsBlogFeed';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import { Typography } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CtaButton from 'components/CtaButton';

const MarketNewsFeedBlockContainer = styled(Box)`
  padding: 5rem;

  ${({ theme }) => theme.breakpoints.down('lg')} {
    padding: 3rem 1rem;
  }
`;

const Header = styled(Typography)`
  text-transform: uppercase;
  border-bottom: 3px solid #e21f26;
  padding-bottom: .33rem;
  font-size: 1.25rem;
  font-weight: 700;
  display: inline-block;
  margin-bottom: 1rem;

  ${({ theme }) => theme.breakpoints.down('md')} {
    margin-bottom: 0.5rem;
  }
`;

const ShowMore = styled.div`
  display: flex;
  justify-content: center;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  bottom: -1.5rem;

  svg {
    width: 3rem;
    height: 3rem;
    padding: 0.5rem;
    border: 0.0625rem solid ${prop('theme.colors.lightGray')};
    border-radius: 50%;
    cursor: pointer;
    background-color: ${prop('theme.colors.white')};
    color: ${prop('theme.colors.black')};
    transition: transform 0.3s;
    transform: ${({ rotated }) => (rotated ? 'rotate(180deg)' : 'rotate(0deg)')};

    &:hover {
      color: ${prop('theme.colors.ttRed')};
    }
  }
`;

const BlogPostFeedWrapper = styled(Grid)`
  ${({ theme }) => theme.breakpoints.down('sm')} {
    border-bottom: 1px solid rgba(29, 25, 27, .1);
    padding: 0;
    margin-bottom: 1rem;
    min-height: 129px;
  }

  ${ifProp('$firstItem', css`
    ${({ theme }) => theme.breakpoints.down('sm')} {
      min-height: 300px;
    }
  `)};
`;

const GradientCover = styled.div`
  width: 100%;
  height: 3rem;
  background-image: linear-gradient(rgba(230, 230, 230, 0), #fafafa 97%);
  position: absolute;
  top: auto;
  bottom: 0%;
  left: 0%;
  right: 0%;
`;

/* 👇 Changed: use auto/visible when expanded or not on phone */
const BlogPostFeedContainer = styled(Box)`
  ${({ $expanded, $isJustInSection, $isPhone }) =>
    $expanded || $isJustInSection || !$isPhone ? css`
    height: auto;
    overflow: visible;
  `: css`
    height: 8rem;
    overflow: hidden;
  `};
`;

const CtaButtonContainer = styled.div`
  text-align: center;
  margin-top: 1.5rem;
`;

function MarketNewsFeedBlock({ data }) {
  const { marketNewsBlogPostFeed, buttonCta } = data;
  const justInSection = 'just in';

  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'xl'));

  const [expandedPosts, setExpandedPosts] = useState([]);

  const isExpanded = (index) => isTablet || expandedPosts.includes(index);

  const handleTogglePostHeight = (index) => {
    setExpandedPosts((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  return (
    <MarketNewsFeedBlockContainer>
      <Header variant='h2' pt={0}>{data.header}</Header>
      <Grid container spacing={isPhone ? 0 : 3}>
        {
          marketNewsBlogPostFeed.map((post, index) => (
          <BlogPostFeedWrapper
            item
            xs={12}
            sm={6}
            md={3}
            lg={3}
            key={index}
            $firstItem={index === 0}
          >
            <Box position='relative'>
              <BlogPostFeedContainer
                key={index}
                $expanded={isExpanded(index)}
                $isJustInSection={post.header.toLowerCase() === justInSection}
                $isPhone={isPhone}
              >
                <NewsBlogFeed
                  key={index}
                  display='grid'
                  useSeoData={false}
                  blogPostFeed={post}
                />
              </BlogPostFeedContainer>

              {post.header.toLowerCase() !== justInSection && isPhone && (
                <ShowMore
                  rotated={isExpanded(index)}
                  onClick={() => handleTogglePostHeight(index)}
                  aria-expanded={isExpanded(index)}
                >
                  <KeyboardArrowDownIcon />
                </ShowMore>
              )}
            </Box>
          </BlogPostFeedWrapper>
        ))}
      </Grid>

      <CtaButtonContainer style={{ textAlign: 'center' }}>
        <CtaButton data={buttonCta} />
      </CtaButtonContainer>
    </MarketNewsFeedBlockContainer>
  );
}

export default MarketNewsFeedBlock;
